// =============================================================================
// io/sync/goals.ts — the period targets.
// -----------------------------------------------------------------------------
// A small catalogue: four rows on Josh's account, seeded by the onboarding
// template. Read once when the Dashboard mounts.
//
// ── ⚠ ORDERED BY `id`, AND THAT IS NOT ARBITRARY EVEN THOUGH THE IDS ARE ──
//
// The old client's `selectAllRows` defaults its page key to `id`, so the goals
// list is ordered by a random `uid()` string — which produces a stable but
// meaningless sequence. On Josh's book that is Weekly, Daily, Monthly, Yearly,
// and his screenshot shows exactly that order.
//
// Ordering by `created_at` instead — the obvious "sensible" choice — puts them
// in Daily, Weekly, Monthly, Yearly and silently disagrees with the screen the
// port is being checked against. Matching beta here costs nothing and removes a
// difference that would otherwise be read as a bug in the card.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Goal } from '../../domain/goals';
import { PAGE_ROWS } from './load';

const GOAL_COLUMNS = ['id', 'name', 'template', 'period', 'params', 'account_ids'] as const;
export const GOALS_SELECT = GOAL_COLUMNS.join(',');

export function goalFromDb(r: Record<string, unknown>): Goal {
  return {
    id: String(r['id'] ?? ''),
    name: String(r['name'] ?? ''),
    template: String(r['template'] ?? ''),
    period: String(r['period'] ?? ''),
    // ⚠ `params` is jsonb and can legitimately be null on a half-created row.
    params: (r['params'] ?? {}) as Record<string, unknown>,
    // ⚠ `account_ids` is a TEXT[] with an array default, but a row written by an
    // older client can still carry null. An empty array means "all accounts",
    // and null must resolve to that rather than crash the `.some()` below it.
    accountIds: Array.isArray(r['account_ids']) ? (r['account_ids'] as string[]) : [],
  };
}

/**
 * Every goal, in beta's order.
 *
 * ⚠ Pages to exhaustion like every other read here. Four rows today, and the
 * reason this is not a single `.range(0, 999)` is the same as everywhere else:
 * the correct-looking shortcut is invisible until the day it truncates.
 */
export async function loadGoals(sb: SupabaseClient): Promise<Goal[]> {
  const out: Goal[] = [];

  for (let from = 0; ; from += PAGE_ROWS) {
    const { data, error } = await sb
      .from('goals')
      .select(GOALS_SELECT)
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);

    // ⚠ Throws rather than returning a short list. A missing goal is a target
    // the trader is measuring themselves against and cannot see — and an absent
    // row looks identical to one they never created.
    if (error) throw new Error(`goals read failed on page ${(from / PAGE_ROWS) + 1}: ${error.message}`);
    if (!data || !data.length) break;

    for (const r of data as unknown as Record<string, unknown>[]) out.push(goalFromDb(r));
    if (data.length < PAGE_ROWS) break;
  }

  return out;
}
